/**
 * Client-side state: the trip definition (immutable per session) + live
 * slots/checks. Local edits are applied optimistically and sent via sync;
 * remote batches arrive through the same applyChanges path.
 */
import type { ChangeEntry, HistoryEntry, Role } from "@trip/shared/protocol";
import type { Checks, DayEnds, FixDurs, Slots, TripDefinition } from "@trip/shared/types";

export interface ChangeEvent {
  slotKeys: string[];
  checksChanged: boolean;
  dayEndsChanged: boolean;
  fixDursChanged: boolean;
  /** remote edits carry attribution for the toast */
  editor?: string;
  remote: boolean;
}

type Listener = (e: ChangeEvent) => void;

class Store {
  def!: TripDefinition;
  slots: Slots = {};
  checks: Checks = {};
  dayEnds: DayEnds = {};
  fixDurs: FixDurs = {};
  rev = 0;
  role: Role = "read";
  tripId = "";
  tripName = "";
  key: string | null = null;
  /** display name derived server-side from the key/session (welcome + trip fetch) */
  editor = "";
  online = false;
  presence: string[] = [];

  private listeners = new Set<Listener>();

  init(tripId: string, key: string | null, def: TripDefinition, slots: Slots, checks: Checks, dayEnds: DayEnds, fixDurs: FixDurs, rev: number, role: Role, name: string, editor: string) {
    this.tripId = tripId;
    this.key = key;
    this.def = def;
    this.slots = slots;
    this.checks = checks;
    this.dayEnds = dayEnds;
    this.fixDurs = fixDurs;
    this.rev = rev;
    this.role = role;
    this.tripName = name;
    this.editor = editor;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Applies a batch (local-optimistic or remote) and notifies subscribers. */
  applyChanges(entries: ChangeEntry[], opts: { remote: boolean; rev?: number; editor?: string }) {
    const slotKeys: string[] = [];
    let checksChanged = false;
    let dayEndsChanged = false;
    let fixDursChanged = false;
    for (const ch of entries) {
      if (ch.kind === "slot") {
        this.slots[ch.key] = ch.value as Slots[string];
        slotKeys.push(ch.key);
      } else if (ch.kind === "dayend") {
        this.dayEnds[ch.key] = (ch.value as { min: number }).min;
        dayEndsChanged = true;
      } else if (ch.kind === "fixdur") {
        this.fixDurs[ch.key] = (ch.value as { min: number }).min;
        fixDursChanged = true;
      } else {
        this.checks[ch.key] = (ch.value as { on: boolean }).on;
        checksChanged = true;
      }
    }
    if (opts.rev !== undefined) this.rev = opts.rev;
    const evt: ChangeEvent = { slotKeys, checksChanged, dayEndsChanged, fixDursChanged, editor: opts.editor, remote: opts.remote };
    for (const fn of this.listeners) fn(evt);
  }

  canEdit(): boolean {
    return this.role === "edit" && this.online;
  }
}

export const store = new Store();
